/**
 * The Inspector's fields for OP-Motion's landing presets (2026-10-24, OPM4LBuilder's ask): a Capture step (which bus,
 * and whether a press makes a new preset or fills one) and a Landing preset step (which preset, its name and what it
 * holds, capture it again, delete it). A Landing preset button with nothing picked offers "Capture now", so the
 * shortest way to a preset is: add the button, dial OP-Motion in, tap Capture now.
 */

import { createSignal, For, Show, type JSX } from 'solid-js';
import type { OpMotionCaptureStep, OpMotionLandStep } from '@opbuttons/schema';
import { activeMotionPresets, motionPresetById, removeMotionPreset, renameMotionPreset } from '../layout/projectStore.ts';
import { fireAction } from '../opbridge/dispatch.ts';
import { MOTION_BUSES, motionBusLabel, motionPairsText } from '../timeline/opMotion.ts';
import { pushToast } from '../ui/toast.ts';
import { ulid } from '../utils/ulid.ts';
import { InfoHint } from './InfoHint.tsx';
import { Tip, TipRow } from './Tip.tsx';

/** Ask OP-Motion for what it is set to, into `into` (made with that id if it is new). The answer is stored by
 *  `timeline/motionCapture.ts`, which toasts what it saved or that nothing answered. */
async function captureInto(into: string, target: string): Promise<void> {
  const res = await fireAction({ id: ulid(), target, type: 'opmotion.capture', bus: 'all', into });
  const bad = res.find((r) => r.kind !== 'ok');
  if (bad) pushToast('error', `Capture not sent: ${bad.detail ?? bad.kind}`, 4000);
}

function ago(ms: number): string {
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return new Date(ms).toLocaleDateString();
}

export function MotionCaptureFields(props: { step: OpMotionCaptureStep; onPatch: (p: Partial<OpMotionCaptureStep>) => void }): JSX.Element {
  return (
    <>
      <label class="inspector-field">
        <span>Capture</span>
        <select data-motion-capture-bus value={props.step.bus || 'all'} onChange={(e) => props.onPatch({ bus: e.currentTarget.value })}>
          <option value="all">Every bus</option>
          <For each={MOTION_BUSES}>{(b) => <option value={b.id}>{b.label}</option>}</For>
        </select>
      </label>
      <label class="inspector-field">
        <span>Saves into</span>
        <select data-motion-capture-into value={props.step.into ?? ''} onChange={(e) => props.onPatch({ into: e.currentTarget.value || undefined })}>
          <option value="">A new preset each press</option>
          <For each={activeMotionPresets()}>{(p) => <option value={p.id}>{p.name}</option>}</For>
        </select>
      </label>
      <InfoHint title="Capture" label="What this does" short="Needs OP-Motion v0.3 in your set.">
        <p>Saves what OP-Motion on {motionBusLabel(props.step.bus || 'all')} is set to right now (each effect's value,
        its motion, rate and depth) as a landing preset. A Landing preset button then lands it.</p>
        <p>It reads what each effect is set to, not the moving sound. Moves and the crossfader are left out, so a
        preset never switches A and B by itself.</p>
        <p>It works as a tap only: OPButtons stores the answer, so a timeline or a pedal cannot capture.</p>
      </InfoHint>
    </>
  );
}

export function MotionLandFields(props: { step: OpMotionLandStep; target: string; onPatch: (p: Partial<OpMotionLandStep>) => void }): JSX.Element {
  const preset = () => motionPresetById(props.step.presetId);
  const [confirmDelete, setConfirmDelete] = createSignal(false);
  let confirmTimer: ReturnType<typeof setTimeout> | undefined;
  const captureNow = (): void => {
    const id = props.step.presetId || `motion-${ulid()}`;
    if (!props.step.presetId) props.onPatch({ presetId: id });
    void captureInto(id, props.target);
  };
  const del = (): void => {
    const p = preset();
    if (!p) return;
    if (!confirmDelete()) {
      setConfirmDelete(true);
      clearTimeout(confirmTimer);
      confirmTimer = setTimeout(() => setConfirmDelete(false), 3000);
      return;
    }
    setConfirmDelete(false);
    removeMotionPreset(p.id);
    props.onPatch({ presetId: '' });
  };
  return (
    <>
      <label class="inspector-field">
        <span>Preset</span>
        <select data-motion-land-preset value={preset() ? props.step.presetId : ''} onChange={(e) => props.onPatch({ presetId: e.currentTarget.value })}>
          <option value="">None yet</option>
          <For each={activeMotionPresets()}>{(p) => <option value={p.id}>{p.name}</option>}</For>
        </select>
      </label>
      <Show when={preset()} fallback={
        <>
          <div class="inspector-pills">
            <button type="button" class="inspector-pill" data-motion-capture-now onClick={captureNow}
              title="Save what OP-Motion is set to now on every bus, as this button's preset">Capture now</button>
          </div>
          <TipRow label="How" text="Dial OP-Motion in on every bus you want, then capture it here." />
        </>
      }>
        {(p) => (
          <>
            <label class="inspector-field">
              <span>Name</span>
              <input type="text" data-motion-preset-name value={p().name}
                onChange={(e) => renameMotionPreset(p().id, e.currentTarget.value)} />
            </label>
            <div class="inspector-motion-preset" data-motion-preset-summary>
              <For each={Object.keys(p().buses)}>
                {(bus) => <span class="inspector-hint"><b>{motionBusLabel(bus)}</b>: {motionPairsText(p().buses[bus] ?? [])}</span>}
              </For>
              <span class="inspector-hint">Captured {ago(p().capturedAt)}</span>
            </div>
            <div class="inspector-pills">
              <button type="button" class="inspector-pill" data-motion-capture-now onClick={captureNow}
                title="Replace this preset with what OP-Motion is set to now">Capture again</button>
              <button type="button" class="inspector-pill" data-motion-preset-delete onClick={del}
                title="Delete this preset from the project">{confirmDelete() ? 'Tap again to delete' : 'Delete preset'}</button>
            </div>
          </>
        )}
      </Show>
      <InfoHint title="Landing preset" label="What this does" short="Lands on the next bar.">
        <p>Everything the preset holds glides from where it is and arrives on the next downbeat, on each bus it was
        captured from. Fields it does not name keep their value.</p>
        <p>Put this button on a timeline to land the preset at that bar. A preset never moves the crossfader.</p>
      </InfoHint>
    </>
  );
}
